import { useAuth } from "@clerk/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FEATURE_FLAGS } from "../../../shared/features";
import {
  metricsQueryEnabled,
  projectMetricsState,
  rdMetricsQueryOptions,
  rosterMetricsQueryOptions,
  type CompactRdMetrics,
  type MetricsFeatureInput,
  type MetricsState,
  type RdMetrics,
  type RdMetricsNotPublished,
  type RosterMetricsRow,
} from "./metrics-cache";
import type { RosterScope } from "./roster-scope-state";
import { useFeatures } from "./use-features";

/*
 * The one client entry point for RD metrics. Keys, query options, roster
 * seeding, and lifecycle projection live in `./metrics-cache` (pure, tested);
 * these hooks only wire auth, the feature flag, and the QueryClient into them.
 */
export {
  METRICS_CACHE_MS,
  isRosterRowNotPublished,
  metricsKeys,
  projectMetricsState,
  type CompactMetricEnvelope,
  type CompactRdMetrics,
  type MetricEnvelope,
  type MetricName,
  type MetricsErrorKind,
  type MetricsState,
  type NotPublishedRosterRow,
  type PublishedRosterRow,
  type RdMetrics,
  type RosterMetricsRow,
} from "./metrics-cache";

export interface RdMetricsOptions {
  /** Fetch the full history (series) instead of the summary the roster already seeded. */
  detail?: boolean;
}

function useMetricsGate() {
  const { getToken, isLoaded, isSignedIn } = useAuth({
    treatPendingAsSignedOut: false,
  });
  const featuresQuery = useFeatures();
  const features: MetricsFeatureInput = {
    status: featuresQuery.status,
    flagOn: featuresQuery.data?.data[FEATURE_FLAGS.supervisorMetrics] === true,
  };
  const auth = { isLoaded, isSignedIn: isSignedIn === true };

  return { getToken, auth, features, enabled: metricsQueryEnabled({ auth, features }) };
}

export function useRdMetrics(
  rdId: string,
  options: RdMetricsOptions & { detail: true }
): MetricsState<RdMetrics>;
export function useRdMetrics(
  rdId: string,
  options?: RdMetricsOptions & { detail?: false }
): MetricsState<CompactRdMetrics>;

export function useRdMetrics(
  rdId: string,
  options: RdMetricsOptions = {}
): MetricsState<RdMetrics> | MetricsState<CompactRdMetrics> {
  const queryClient = useQueryClient();
  const { getToken, auth, features, enabled } = useMetricsGate();
  const query = useQuery({
    ...rdMetricsQueryOptions(rdId, { getToken, queryClient }, options.detail === true),
    enabled,
  });

  return projectMetricsState<RdMetrics | CompactRdMetrics | RdMetricsNotPublished>({
    auth,
    features,
    query: {
      status: query.status,
      data: query.data,
      error: query.error,
      refetch: query.refetch,
    },
  }) as MetricsState<RdMetrics> | MetricsState<CompactRdMetrics>;
}

export function useRosterMetrics(scope: RosterScope): MetricsState<RosterMetricsRow[]> {
  const queryClient = useQueryClient();
  const { getToken, auth, features, enabled } = useMetricsGate();
  const query = useQuery({
    ...rosterMetricsQueryOptions(scope, { getToken, queryClient }),
    enabled,
  });

  return projectMetricsState({
    auth,
    features,
    query: {
      status: query.status,
      data: query.data,
      error: query.error,
      refetch: query.refetch,
    },
  });
}
